import { useCallback, useEffect, useMemo, useState } from "react";
import type { Canal, Conexion, FlujoDetalle, Notificacion } from "../services/notificaciones-service";
import {
  clavesDe,
  conectar,
  desdeVersion,
  hayCambios,
  notificacionNueva,
  nuevaClave,
  quitarNodo,
  type EstadoLienzo,
  type Posicion,
} from "../utils/lienzo";

export type Seleccion =
  | { tipo: "evento"; clave: string }
  | { tipo: "notificacion"; clave: string }
  | { tipo: "conexion"; clave: string }
  | null;

/**
 * Estado editable del lienzo (copia local del borrador) + la línea base guardada para saber
 * si hay cambios. Cuando el servidor devuelve un borrador nuevo (guardar/publicar) se re-sincroniza.
 */
export function useEstadoDisenador(detalle: FlujoDetalle) {
  const base = useMemo(() => desdeVersion(detalle.borrador), [detalle.borrador]);
  const [estado, setEstado] = useState<EstadoLienzo>(base);
  const [seleccion, setSeleccion] = useState<Seleccion>(null);

  useEffect(() => {
    setEstado(base);
  }, [base]);

  const sucio = hayCambios(estado, base);

  const agregarEvento = useCallback((tipoEvento: string, posicion?: Posicion) => {
    setEstado((e) => {
      const clave = nuevaClave("evento", clavesDe(e));
      const pos = posicion ?? { x: 0, y: e.eventos.length * 150 };
      setSeleccion({ tipo: "evento", clave });
      return { ...e, eventos: [...e.eventos, { clave, tipoEvento }], posiciones: { ...e.posiciones, [clave]: pos } };
    });
  }, []);

  const agregarNotificacion = useCallback((canal: Canal, posicion?: Posicion) => {
    setEstado((e) => {
      const clave = nuevaClave(canal.toLowerCase(), clavesDe(e));
      const pos = posicion ?? { x: 440, y: e.notificaciones.length * 150 };
      setSeleccion({ tipo: "notificacion", clave });
      return {
        ...e,
        notificaciones: [...e.notificaciones, notificacionNueva(canal, clave)],
        posiciones: { ...e.posiciones, [clave]: pos },
      };
    });
  }, []);

  const moverNodo = useCallback((clave: string, posicion: Posicion) => {
    setEstado((e) => ({ ...e, posiciones: { ...e.posiciones, [clave]: posicion } }));
  }, []);

  const quitar = useCallback((clave: string) => {
    setEstado((e) =>
      e.conexiones.some((c) => c.clave === clave)
        ? { ...e, conexiones: e.conexiones.filter((c) => c.clave !== clave) }
        : quitarNodo(e, clave),
    );
    setSeleccion((s) => (s?.clave === clave ? null : s));
  }, []);

  const unir = useCallback((eventoClave: string, notificacionClave: string) => {
    setEstado((e) => conectar(e, eventoClave, notificacionClave));
  }, []);

  const actualizarNotificacion = useCallback((clave: string, cambio: Partial<Notificacion>) => {
    setEstado((e) => ({
      ...e,
      notificaciones: e.notificaciones.map((n) => (n.clave === clave ? { ...n, ...cambio } : n)),
    }));
  }, []);

  const actualizarConexion = useCallback((clave: string, cambio: Partial<Conexion>) => {
    setEstado((e) => ({ ...e, conexiones: e.conexiones.map((c) => (c.clave === clave ? { ...c, ...cambio } : c)) }));
  }, []);

  const descartar = useCallback(() => {
    setEstado(base);
    setSeleccion(null);
  }, [base]);

  return {
    estado,
    sucio,
    seleccion,
    setSeleccion,
    agregarEvento,
    agregarNotificacion,
    moverNodo,
    quitar,
    unir,
    actualizarNotificacion,
    actualizarConexion,
    descartar,
  };
}

export type EstadoDisenador = ReturnType<typeof useEstadoDisenador>;
